import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import * as XLSX from 'xlsx';
import { notify } from '../../common/functions/react-toastify.function';
import { bulkDeleteFiberPopSites } from './functions/bulk_delete_fiber_pop_sites.function';

const FIBER_POP_COLUMN_KEYS = [
  'fiber_pop',
  'Fiber Pop',
  'Fiber POP',
  'FiberPop',
  'fiber pop',
  'FIBER_POP',
];

const findFiberPopColumnKey = (row) => {
  const keys = Object.keys(row || {});
  for (const possible of FIBER_POP_COLUMN_KEYS) {
    const found = keys.find((k) => k.trim().toLowerCase() === possible.trim().toLowerCase());
    if (found) {
      return found;
    }
  }
  return keys[0] || null;
};

const normalizeFiberPop = (v) => {
  if (v == null) {
    return '';
  }
  return String(v).trim().toLowerCase();
};

const FiberPopBulkDeleteModal = ({ open, onClose, fiberPopData = [], onDeleteSuccess }) => {
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [parsedFiberPops, setParsedFiberPops] = useState([]);
  const [foundInSystem, setFoundInSystem] = useState([]);
  const [notInSystem, setNotInSystem] = useState([]);
  const [columnKey, setColumnKey] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!open) {
      setFile(null);
      setFileName('');
      setParsedFiberPops([]);
      setFoundInSystem([]);
      setNotInSystem([]);
      setColumnKey(null);
      setIsProcessing(false);
      setIsDeleting(false);
      setErrorMessage('');
    }
  }, [open]);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    event.target.value = '';
    if (!selectedFile) {
      return;
    }

    const ext = selectedFile.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls'].includes(ext)) {
      notify('error', 'Please upload only an Excel file (.xlsx or .xls).');
      return;
    }

    setFile(selectedFile);
    setFileName(selectedFile.name);
    setErrorMessage('');
    setFoundInSystem([]);
    setNotInSystem([]);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { raw: true });

        if (!jsonData.length) {
          setErrorMessage('File has no rows.');
          setParsedFiberPops([]);
          setColumnKey(null);
          return;
        }

        const firstRow = jsonData[0];
        const key = findFiberPopColumnKey(firstRow);
        if (!key) {
          setErrorMessage(
            'No "Fiber Pop" column found in the file. Please use a file that contains only Fiber Pop column.'
          );
          setParsedFiberPops([]);
          setColumnKey(null);
          return;
        }

        setColumnKey(key);
        const values = [];
        jsonData.forEach((row) => {
          const val = row[key];
          if (val != null && String(val).trim() !== '') {
            values.push(String(val).trim());
          }
        });
        setParsedFiberPops(values);
        setIsProcessing(true);
      } catch (err) {
        console.error('Error reading Excel:', err);
        setErrorMessage('Error reading Excel file. Please check the file format.');
        setParsedFiberPops([]);
        setColumnKey(null);
      }
    };
    reader.readAsArrayBuffer(selectedFile);
  };

  useEffect(() => {
    if (!isProcessing || !parsedFiberPops.length || !fiberPopData.length || !columnKey) {
      if (isProcessing && parsedFiberPops.length && fiberPopData.length) {
        setIsProcessing(false);
      }
      return;
    }

    const popMap = new Map();
    fiberPopData.forEach((pop) => {
      const fp = pop.fiber_pop;
      if (fp != null && fp !== '--') {
        const norm = normalizeFiberPop(fp);
        if (norm && !popMap.has(norm)) {
          popMap.set(norm, { id: pop.id, fiber_pop: fp });
        }
      }
    });

    const found = [];
    const notFound = [];
    const seenNorm = new Set();
    parsedFiberPops.forEach((value) => {
      const norm = normalizeFiberPop(value);
      if (!norm) {
        return;
      }
      if (seenNorm.has(norm)) {
        return;
      }
      seenNorm.add(norm);
      const match = popMap.get(norm);
      if (match) {
        found.push(match);
      } else {
        notFound.push(value);
      }
    });

    setFoundInSystem(found);
    setNotInSystem(notFound);
    setIsProcessing(false);
  }, [isProcessing, parsedFiberPops, fiberPopData, columnKey]);

  const handleConfirmDelete = async () => {
    if (foundInSystem.length === 0) {
      notify('warning', 'No Fiber POPs from the file are present in the system to delete.');
      return;
    }

    setIsDeleting(true);
    try {
      const ids = foundInSystem.map((x) => x.id);
      await bulkDeleteFiberPopSites(ids);
      notify('success', `Successfully deleted ${ids.length} Fiber POP(s).`);
      if (onDeleteSuccess) {
        onDeleteSuccess(ids);
      }
      onClose?.();
    } catch (e) {
      notify('error', 'Failed to delete Fiber POPs. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const canClose = !isProcessing && !isDeleting;
  const hasResults = foundInSystem.length > 0 || notInSystem.length > 0;

  return (
    <Dialog
      open={open}
      onClose={canClose ? onClose : undefined}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: '12px' } }}
      disableEscapeKeyDown={!canClose}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Bulk Delete
        </Typography>
        {canClose && (
          <IconButton size="small" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload a file that contains only <strong>Fiber Pop</strong> column.
        </Typography>

        <Button
          variant="outlined"
          component="label"
          fullWidth
          sx={{ mb: 2 }}
          disabled={isProcessing || isDeleting}
        >
          {fileName || 'Choose Excel file (.xlsx, .xls)'}
          <input
            type="file"
            hidden
            accept=".xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={handleFileChange}
          />
        </Button>

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        {isProcessing && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Checking file against system data…</Typography>
          </Box>
        )}

        {!isProcessing && hasResults && (
          <Box sx={{ mt: 2 }}>
            {foundInSystem.length > 0 && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>{foundInSystem.length}</strong> Fiber POP(s) found in system and will be
                deleted.
              </Typography>
            )}
            {notInSystem.length > 0 && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  These Fiber POPs are not in our system:
                </Typography>
                <Typography variant="body2" component="span">
                  {notInSystem.slice(0, 20).join(', ')}
                  {notInSystem.length > 20 ? ` (and ${notInSystem.length - 20} more)` : ''}
                </Typography>
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={!canClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          startIcon={
            isDeleting ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />
          }
          onClick={handleConfirmDelete}
          disabled={foundInSystem.length === 0 || isDeleting || isProcessing}
        >
          {isDeleting ? 'Deleting…' : 'Delete from system'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FiberPopBulkDeleteModal;
